"use client";

import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type ReactNode } from "react";
import {
  SymAufgabenliste,
  SymListe,
  SymNummerierung,
  SymTrennlinie,
  SymUeberschrift1,
  SymUeberschrift2,
  SymUeberschrift3,
  SymZitat,
} from "../symbole";
import { SymInfo, SymKalender, SymSeite, SymTipp, SymWarnung, SymWichtig } from "../lesen/lese-symbole";
import { SymAt, SymBildEinfuegen, SymCodeBlock, SymTabelle, SymVerzeichnis } from "./bearbeiten-symbole";

/*
 * Schwebende Auswahllisten des Bearbeiten-Pfads (Paket E-W2): das Slash-Menü im vollen Umfang und die `@`-Auswahl der Personen.
 * Beide schweben am Cursor (Portal und Position setzt `@horn/editor`); der Fokus bleibt im Editor, deshalb bedient der Editor
 * die Tastatur über `ref.onKeyDown` (Pfeil hoch/runter, Enter; Esc schließt im Editor). Wahl per Maus über `onMouseDown` (der
 * Editor behält den Fokus). Fläche und Einträge wie der Auswahl-Block der Preline-Auswahlmenüs (`view-mode-select`, wie
 * `AnsichtWahl` und `SlashMenue`); Aufbau nach dem Zusammenbau `w4-popups` (Symbol-Kachel mit Zweitzeile, Initialen bei Personen),
 * Abweichung AW-064 (4).
 */

const FLAECHE_KLASSE = "w-64 max-h-80 overflow-y-auto p-1 space-y-0.5 bg-select border border-select-line rounded-xl shadow-xl";
const EINTRAG_KLASSE =
  "py-2 px-3 w-full flex items-center gap-x-3 text-sm text-start text-select-item-foreground cursor-pointer hover:bg-select-item-hover rounded-lg";
const EINTRAG_AKTIV_KLASSE =
  "py-2 px-3 w-full flex items-center gap-x-3 text-sm text-start text-select-item-foreground cursor-pointer bg-select-item-active hover:bg-select-item-hover rounded-lg";
const LEER_KLASSE = "py-2 px-3 text-sm text-muted-foreground-1";
const KACHEL_KLASSE = "size-9 shrink-0 inline-flex items-center justify-center rounded-lg border border-layer-line bg-layer text-muted-foreground-1";
const KOPF_KLASSE = "px-3 pt-2 pb-1 text-xs text-muted-foreground-1";

/** Eintrag des vollen Slash-Menüs (so viel kennt die Darstellung; `@horn/editor` reicht mehr durch). */
export interface SlashVollPunkt {
  key: string;
  label: string;
}

const SLASH_SYMBOL: Record<string, ReactNode> = {
  h1: <SymUeberschrift1 />,
  h2: <SymUeberschrift2 />,
  h3: <SymUeberschrift3 />,
  list: <SymListe />,
  ordered: <SymNummerierung />,
  checklist: <SymAufgabenliste />,
  quote: <SymZitat />,
  hr: <SymTrennlinie />,
  table: <SymTabelle />,
  image: <SymBildEinfuegen />,
  seite: <SymSeite />,
  person: <SymAt />,
  hinweis: <SymInfo />,
  tipp: <SymTipp />,
  warnung: <SymWarnung />,
  wichtig: <SymWichtig />,
  datum: <SymKalender />,
  "wiki-subtree": <SymVerzeichnis />,
  code: <SymCodeBlock />,
};

/** Zweitzeile je Eintrag (Wortlaut nach dem Zusammenbau `w4-popups`, wo er dort steht; sonst kurz und sachlich). */
export const SLASH_ZWEITZEILE: Record<string, string> = {
  table: "Gitter wählen",
  checklist: "Aufgabenliste",
  image: "Aus der Ablage des Raums",
  "wiki-subtree": "Zeigt die Unterseiten live",
  seite: "Verweis auf eine Wiki-Seite",
  person: "Mit @ erwähnen",
  datum: "Mit Uhrzeit möglich",
  hinweis: "Hinweisfeld",
  tipp: "Hinweisfeld",
  warnung: "Hinweisfeld",
  wichtig: "Hinweisfeld",
  code: "Code als Block",
};

export interface ListeRef {
  onKeyDown: (opts: { event: KeyboardEvent }) => boolean;
}

/** Gemeinsame Tastatur: Pfeil hoch/runter wandert, Enter wählt. */
function useAuswahl<T>(items: T[], command: (item: T) => void, ref: React.Ref<ListeRef>) {
  const [index, setIndex] = useState(0);
  const aktiv = useRef<HTMLDivElement | null>(null);
  useEffect(() => setIndex(0), [items]);
  useEffect(() => {
    aktiv.current?.scrollIntoView?.({ block: "nearest" });
  }, [index]);
  useImperativeHandle(ref, () => ({
    onKeyDown({ event }) {
      if (event.key === "ArrowDown") {
        setIndex((i) => (i + 1) % Math.max(items.length, 1));
        return true;
      }
      if (event.key === "ArrowUp") {
        setIndex((i) => (i - 1 + items.length) % Math.max(items.length, 1));
        return true;
      }
      if (event.key === "Enter") {
        const punkt = items[index];
        if (punkt) command(punkt);
        return true;
      }
      return false;
    },
  }));
  return { index, aktiv };
}

export const SlashMenueVoll = forwardRef<ListeRef, { items: SlashVollPunkt[]; command(item: SlashVollPunkt): void }>(function SlashMenueVoll(
  { items, command },
  ref,
) {
  const { index, aktiv } = useAuswahl(items, command, ref);
  return (
    <>
      {/* Preline: ui-components/ui-components-sub-menus-selection/ui-components-cat-select-menus/view-mode-select */}
      <div className={FLAECHE_KLASSE} role="listbox" aria-label="Textbausteine">
        {items.length === 0 && <div className={LEER_KLASSE}>Keine Treffer</div>}
        {items.map((punkt, i) => (
          <div
            key={punkt.key}
            ref={i === index ? aktiv : null}
            role="option"
            aria-selected={i === index}
            className={i === index ? EINTRAG_AKTIV_KLASSE : EINTRAG_KLASSE}
            onMouseDown={(e) => {
              e.preventDefault();
              command(punkt);
            }}
          >
            <span className={KACHEL_KLASSE}>{SLASH_SYMBOL[punkt.key] ?? null}</span>
            <span className="min-w-0 grow">
              <span className="block truncate text-foreground">{punkt.label}</span>
              {SLASH_ZWEITZEILE[punkt.key] ? (
                <span className="block truncate text-xs text-muted-foreground-1">{SLASH_ZWEITZEILE[punkt.key]}</span>
              ) : null}
            </span>
          </div>
        ))}
      </div>
    </>
  );
});

/** Treffer der Personensuche (gleiche Form wie `WikiMentionHit` in `@horn/editor`; keine E-Mail). */
export interface PersonenTreffer {
  id: string;
  name: string;
  subtitle?: string;
  inactive?: boolean;
}

function Initialen({ name }: { name: string }) {
  const teile = name.trim().split(/\s+/).filter(Boolean);
  const buchstaben = (teile.length > 1 ? [teile[0], teile[teile.length - 1]] : teile.slice(0, 1))
    .map((t) => Array.from(t ?? "")[0] ?? "")
    .join("")
    .toUpperCase();
  return (
    <span
      className="size-6 shrink-0 inline-flex items-center justify-center rounded-full bg-primary-100 text-primary-800 text-xs font-semibold"
      aria-hidden="true"
    >
      {buchstaben}
    </span>
  );
}

export const PersonenListe = forwardRef<ListeRef, { items: PersonenTreffer[]; command(item: PersonenTreffer): void }>(function PersonenListe(
  { items, command },
  ref,
) {
  const { index, aktiv } = useAuswahl(items, command, ref);
  return (
    <>
      {/* Preline: ui-components/ui-components-sub-menus-selection/ui-components-cat-select-menus/view-mode-select */}
      <div className={FLAECHE_KLASSE} role="listbox" aria-label="Personen">
        <div className={KOPF_KLASSE}>Personen</div>
        {items.length === 0 && <div className={LEER_KLASSE}>Keine Treffer</div>}
        {items.map((p, i) => (
          <div
            key={p.id}
            ref={i === index ? aktiv : null}
            role="option"
            aria-selected={i === index}
            className={i === index ? EINTRAG_AKTIV_KLASSE : EINTRAG_KLASSE}
            onMouseDown={(e) => {
              e.preventDefault();
              command(p);
            }}
          >
            <Initialen name={p.name} />
            <span className="min-w-0 grow">
              <span className="block truncate text-foreground">{p.name}</span>
              {p.subtitle || p.inactive ? (
                <span className="block truncate text-xs text-muted-foreground-1">
                  {[p.subtitle, p.inactive ? "nicht mehr aktiv" : null].filter(Boolean).join(" · ")}
                </span>
              ) : null}
            </span>
          </div>
        ))}
      </div>
    </>
  );
});
